/**
 * WALL BANDS (2026-10-04, Matt: "add horizontal and vertical bands along wall finishes,
 * similar to how Revit does it and similar to our split face function … different
 * profiles and materials, and I want to start with a row-lock brick course which I will
 * be placing at the top of the stone wainscot").
 *
 * The read side: a wall carries `faceSweeps` (FaceSweepEntry[]): a band on ONE face,
 * horizontal at a height (`at` = feet above the wall base — the face-split convention)
 * or vertical at a station (`at` = feet along the wall path from its start), in a
 * curated PROFILE (never free-drawn — the trim-profile doctrine) and a material (null =
 * the profile's default finish, by name).
 *
 * A band sits ON the face below it: its back meets the face above (or the same face),
 * its front stands `projectFt` proud of the face below. So a row-lock course at the top
 * of a proud stone wainscot caps the stone and slopes back to the brick above.
 */
import type { FaceSweepEntry } from './types'
import type { WallSide } from './wallCladding'

export interface SweepProfileSpec {
  key: string
  name: string
  /** height of the band's front face (feet) */
  heightFt: number
  /** how far the front stands proud of the face below (feet) */
  projectFt: number
  /** the top slopes down toward the front at this angle (a wash) — 0 = flat */
  washDeg: number
  /** a unit joint every this many feet along the band (0 = none) */
  unitFt: number
  /** the finish it is built in when none is picked, by material name */
  defaultMaterial: string
}

/** the curated profiles — more to come (soldier course, belt, water table, quoins…) */
export const SWEEP_PROFILES: Record<string, SweepProfileSpec> = {
  // bricks on edge with their ENDS showing: 3-5/8" tall, 2-1/4" + a 3/8" joint wide,
  // the usual cap on a wainscot or a sill — 1/2" proud, a 10° wash back to the wall
  rowlock: {
    key: 'rowlock', name: 'Row-lock brick course',
    heightFt: 3.625 / 12, projectFt: 0.5 / 12, washDeg: 10, unitFt: (2.25 + 0.375) / 12,
    defaultMaterial: 'Brick Veneer',
  },
  // BAND TYPES (2026-10-05, Matt: "change their type"): bricks stood on end, the long
  // face showing — 7-5/8" tall, 2-1/4" + a 3/8" joint wide, 1/2" proud, level top
  soldier: {
    key: 'soldier', name: 'Soldier brick course',
    heightFt: 7.625 / 12, projectFt: 0.5 / 12, washDeg: 0, unitFt: (2.25 + 0.375) / 12,
    defaultMaterial: 'Brick Veneer',
  },
  // a flat stone belt course — 8" tall, 1" proud, 2'-0" pieces
  stoneBelt: {
    key: 'stoneBelt', name: 'Stone belt course',
    heightFt: 8 / 12, projectFt: 1 / 12, washDeg: 0, unitFt: 2,
    defaultMaterial: 'Stone Veneer, Coursed Ashlar',
  },
  // a stone water table at the base of a wall — 4" tall, 1-1/2" proud, a 20° wash
  waterTable: {
    key: 'waterTable', name: 'Stone water table',
    heightFt: 4 / 12, projectFt: 1.5 / 12, washDeg: 20, unitFt: 2,
    defaultMaterial: 'Stone Veneer, Coursed Ashlar',
  },
}
export const DEFAULT_SWEEP_PROFILE = 'rowlock'

export function sweepProfileOf(key: string | null | undefined): SweepProfileSpec {
  return SWEEP_PROFILES[key || ''] ?? SWEEP_PROFILES[DEFAULT_SWEEP_PROFILE]
}

export function faceSweepsOf(p: Record<string, unknown> | null | undefined): FaceSweepEntry[] {
  const v = p?.faceSweeps
  if (!Array.isArray(v)) return []
  return (v as FaceSweepEntry[]).filter((s) => s && typeof s.id === 'string'
    && (s.side === 'exterior' || s.side === 'interior') && (s.dir === 'h' || s.dir === 'v') && Number.isFinite(s.at))
}

export type { WallSide }
